/**
 * Sequence Management Component
 *
 * List view for managing WhatsApp template sequences
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Typography,
  CircularProgress,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Grid,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  Assessment as StatsIcon,
  PlayArrow as StartIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon
} from '@mui/icons-material';
import {
  TemplateSequence,
  SequenceCreateRequest,
  SequenceStats
} from '../../types/whatsapp-templates';
import whatsappTemplatesApi from '../../services/whatsapp-templates-api';
import SequenceBuilder from './SequenceBuilder';

interface SequenceManagementProps {
  accountId: number;
}

const SequenceManagement: React.FC<SequenceManagementProps> = ({ accountId }) => {
  const [sequences, setSequences] = useState<TemplateSequence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [builderOpen, setBuilderOpen] = useState(false);
  const [selectedSequence, setSelectedSequence] = useState<TemplateSequence | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sequenceToDelete, setSequenceToDelete] = useState<TemplateSequence | null>(null);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [selectedStats, setSelectedStats] = useState<SequenceStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuSequence, setMenuSequence] = useState<TemplateSequence | null>(null);

  // Load sequences
  const loadSequences = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await whatsappTemplatesApi.listSequences(accountId);

      if (response.success) {
        setSequences(response.sequences);
      } else {
        setError('Failed to load sequences');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load sequences');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSequences();
  }, [accountId]);

  // Handlers
  const handleCreateSequence = async (sequenceData: SequenceCreateRequest) => {
    try {
      const response = await whatsappTemplatesApi.createSequence(accountId, sequenceData);

      if (response.success) {
        setBuilderOpen(false);
        setSelectedSequence(null);
        loadSequences();
      }
    } catch (err: any) {
      throw new Error(err.message || 'Failed to create sequence');
    }
  };

  const handleUpdateSequence = async (sequenceData: SequenceCreateRequest) => {
    if (!selectedSequence) return;

    try {
      const response = await whatsappTemplatesApi.updateSequence(
        selectedSequence.id,
        sequenceData
      );

      if (response.success) {
        setBuilderOpen(false);
        setSelectedSequence(null);
        loadSequences();
      }
    } catch (err: any) {
      throw new Error(err.message || 'Failed to update sequence');
    }
  };

  const handleDeleteSequence = async () => {
    if (!sequenceToDelete) return;

    try {
      await whatsappTemplatesApi.deleteSequence(sequenceToDelete.id);
      setDeleteDialogOpen(false);
      setSequenceToDelete(null);
      loadSequences();
    } catch (err: any) {
      setError(err.message || 'Failed to delete sequence');
    }
  };

  const handleViewStats = async (sequence: TemplateSequence) => {
    setLoadingStats(true);
    setStatsDialogOpen(true);

    try {
      const response = await whatsappTemplatesApi.getSequenceStats(sequence.id);

      if (response.success ) {  //&& response.stats) {
        setSelectedStats(response.data || null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load statistics');
    } finally {
      setLoadingStats(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, sequence: TemplateSequence) => {
    setAnchorEl(event.currentTarget);
    setMenuSequence(sequence);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuSequence(null);
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Automated Sequences</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedSequence(null);
            setBuilderOpen(true);
          }}
        >
          Create Sequence
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Sequences Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Steps</TableCell>
              <TableCell>Trigger</TableCell>
              <TableCell>Usage</TableCell>
              <TableCell>Success Rate</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : sequences.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No sequences found. Create your first sequence to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sequences.map((sequence) => {
                const successRate = sequence.usage_count > 0
                  ? ((sequence.success_count / sequence.usage_count) * 100).toFixed(1)
                  : '0';

                return (
                  <TableRow key={sequence.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {sequence.name}
                      </Typography>
                      {sequence.description && (
                        <Typography variant="caption" color="textSecondary">
                          {sequence.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${sequence.sequence_steps.length} steps`}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={sequence.trigger_type} size="small" />
                    </TableCell>
                    <TableCell>
                      {sequence.usage_count} / {sequence.success_count}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LinearProgress
                          variant="determinate"
                          value={Number(successRate)}
                          sx={{ flex: 1, height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="caption">{successRate}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={sequence.is_active ? <ActiveIcon /> : <InactiveIcon />}
                        label={sequence.is_active ? 'Active' : 'Inactive'}
                        color={sequence.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, sequence)}
                      >
                        <MoreIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (menuSequence) {
              setSelectedSequence(menuSequence);
              setBuilderOpen(true);
            }
            handleMenuClose();
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (menuSequence) {
              handleViewStats(menuSequence);
            }
            handleMenuClose();
          }}
        >
          <StatsIcon fontSize="small" sx={{ mr: 1 }} />
          View Statistics
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (menuSequence) {
              setSequenceToDelete(menuSequence);
              setDeleteDialogOpen(true);
            }
            handleMenuClose();
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Sequence Builder Dialog */}
      <Dialog
        open={builderOpen}
        onClose={() => {
          setBuilderOpen(false);
          setSelectedSequence(null);
        }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          {selectedSequence ? 'Edit Sequence' : 'Create New Sequence'}
        </DialogTitle>
        <DialogContent>
          <SequenceBuilder
            accountId={accountId}
            initialSequence={selectedSequence || undefined}
            onSave={selectedSequence ? handleUpdateSequence : handleCreateSequence}
            onCancel={() => {
              setBuilderOpen(false);
              setSelectedSequence(null);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Statistics Dialog */}
      <Dialog
        open={statsDialogOpen}
        onClose={() => {
          setStatsDialogOpen(false);
          setSelectedStats(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Sequence Statistics</DialogTitle>
        <DialogContent>
          {loadingStats ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : selectedStats ? (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {selectedStats.name}
                </Typography>
              </Grid>

              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="textSecondary">
                      Total Started
                    </Typography>
                    <Typography variant="h4">{selectedStats.total_started}</Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="textSecondary">
                      Successes
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {selectedStats.total_success}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="textSecondary">
                      Success Rate
                    </Typography>
                    <Typography variant="h4">{selectedStats.success_rate}%</Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="textSecondary">
                      Active
                    </Typography>
                    <Typography variant="h4" color="primary.main">
                      {selectedStats.active}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Status Breakdown
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Completed
                      </Typography>
                      <Typography variant="h6">{selectedStats.completed}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Stopped
                      </Typography>
                      <Typography variant="h6">{selectedStats.stopped}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Failed
                      </Typography>
                      <Typography variant="h6" color="error.main">
                        {selectedStats.failed}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setStatsDialogOpen(false);
            setSelectedStats(null);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Sequence</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the sequence "{sequenceToDelete?.name}"? This will
            also stop all active instances of this sequence.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteSequence} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SequenceManagement;
